import * as tf from '@tensorflow/tfjs';
import { getSimilarityTransformation } from './dl_allignment';

export const _get_fahrenheit_temperature = (n) => {
  return 68 + 0.18 * n;
};

export const _calculate_B = (thermal_frame, BB_coordinates, config_A, config_T0, BB_temperature) => {
  let frame_bb = getROI(thermal_frame, BB_coordinates);
  let bb_pixel;
  let _data_source = frame_bb.data16U;
  let _i = Math.round(_data_source.length / 2);
  let _s = _data_source.sort();

  // Median calculation
  if (_data_source.length % 2 > 0) {
    bb_pixel = _s[1 + _i];
  } else {
    bb_pixel = (_s[_i] + _s[1 + _i]) / 2;
  }

  let B_value = 1 / (Math.exp(config_T0 / BB_temperature) - 1) - config_A * bb_pixel;

  //Clear all local variables
  frame_bb.delete();
  bb_pixel = null;
  _data_source = null;
  _s = null;
  return B_value;
};

export const _calculate_hist = (thermal_frame, coeff_B, config_A, config_C, config_T0, config_R) => {
  let h = new Array(256).fill(0);
  let width = thermal_frame.cols;
  let height = thermal_frame.rows;

  for (let j = 0; j < height; j++) {
    for (let i = 0; i < width; i++) {
      if (Math.pow((2 * i) / width - 1, 2) + Math.pow((2 * j) / height - 1, 2) <= 1) {
        let S, T;
        let pixel_value = thermal_frame.data16U[j * width + i]; //thermal_frame.row(i).col(j).data16U[0]; Leads to memory leaks
        let temp = config_R / (config_A * pixel_value + coeff_B);

        if (temp > 0) {
          T = config_T0 / Math.log(1 + temp) - 273.15;
        } else {
          T = -273.15;
        }
        S = Math.round(10 * (T - 20));
        if (S < 0 || S > 250) {
          S = 0;
        }

        h[S] += 1;
        pixel_value = null;
        temp = null;
        S = null;
        T = null;
      }
    }
  }

  const cumulativeSum = ((sum) => (value) => (sum += value))(0);
  let histogram_cum_sum = h.reverse().map(cumulativeSum);
  let cut_pixels = Math.round(histogram_cum_sum[histogram_cum_sum.length - 1] / config_C);
  let temp_res;

  for (let i = 0; i < 255; i++) {
    if (histogram_cum_sum[i] > cut_pixels) {
      temp_res = 255 - i;
      break;
    }
  }
  let F_temperature = _get_fahrenheit_temperature(temp_res);

  //Clear all local variables
  h = null;
  histogram_cum_sum = null;
  temp_res = null;
  return F_temperature;
};

export const fix_range = (value, min_value, max_value) => {
  return Math.min(Math.max(value, min_value), max_value);
};

export const getROI = (src_frame, x1y1x2y2) => {
  // eslint-disable-next-line no-undef
  let frame_bb = new cv.Mat();
  let x1 = fix_range(x1y1x2y2[0], 0, src_frame.cols);
  let y1 = fix_range(x1y1x2y2[1], 0, src_frame.rows);
  let x2 = fix_range(x1y1x2y2[2], 0, src_frame.cols);
  let y2 = fix_range(x1y1x2y2[3], 0, src_frame.rows);
  // eslint-disable-next-line no-undef
  let _rect = new cv.Rect(x1, y1, x2 - x1, y2 - y1);
  let src_frame_roi = src_frame.roi(_rect);

  src_frame_roi.copyTo(frame_bb);
  src_frame_roi.delete();
  return frame_bb;
};

export const estimateFaces = async (faceModel, tensor, returnTensors, useMockData = false) => {
  let predictions = null;

  predictions = await faceModel.estimateFaces(tensor, returnTensors);
  if (useMockData) {
    predictions = JSON.parse(
      '[{"topLeft":[26,40],"bottomRight":[52,68],"landmarks":[[174,174],[216,216],[174,174],[216,216],[174,174],[216,216],[174,174],[216,216],[174,174],[216,216]],"probability":[0.987721860408783]}]',
    );
  }

  return predictions;
};

export const calculateTemperatureByThermalData = async (
  thermalData,
  faceModel,
  bbCoordinates,
  faceDetectionImageMinWidth,
  faceDetectionImageMinHeight,
  transformation,
) => {
  let temperature = null;
  // eslint-disable-next-line no-undef
  const src = cv.matFromArray(thermalData.height, thermalData.width, cv.CV_16U, thermalData.data);
  const f_width = src.cols;
  const f_height = src.rows;

  //TODO the normalization matrix should be uploaded one time (unique matrix for each camera)
  let use_frame_normalization = false;
  if (use_frame_normalization) {
    //TODO :: read this matrix from file
    // eslint-disable-next-line no-undef
    let norm_matrix = cv.matFromArray(f_height, f_width, cv.CV_32F, new Array(f_height * f_width).fill(1.0));
    // eslint-disable-next-line no-undef
    const src_float = new cv.Mat();
    // eslint-disable-next-line no-undef
    src.convertTo(src_float, cv.CV_32F);
    //perform elementwise multiplication
    // eslint-disable-next-line no-undef
    cv.multiply(src_float, norm_matrix, src_float);
    // eslint-disable-next-line no-undef
    src_float.convertTo(src, cv.CV_16U);

    norm_matrix.delete();
    src_float.delete();
  }

  // following lines is copy&paste of opencv.js dilate tutorial:
  // eslint-disable-next-line no-undef
  let dst = new cv.Mat();
  // eslint-disable-next-line no-undef
  let in_dst = new cv.Mat();
  // eslint-disable-next-line no-undef
  cv.normalize(src, dst, 0, 255, cv.NORM_MINMAX, cv.CV_8U);
  // eslint-disable-next-line no-undef
  cv.cvtColor(dst, dst, cv.COLOR_GRAY2RGB);

  // Prepare frame : pad to square and resize to Network input
  // If you have a lot of resources you can use correct inference wtih 1:1 network input (correct_input = true; option )
  // If you should run faster use smaller image (correct_input = false; option )
  let correct_input = true;
  let m_x_size;
  let m_y_size;
  let m_c_size = 3;
  let dsize;
  if (correct_input) {
    m_x_size = faceDetectionImageMinWidth;
    m_y_size = faceDetectionImageMinHeight;
    m_c_size = 3;
    // eslint-disable-next-line no-undef
    dsize = new cv.Size(m_x_size, m_y_size);
    // eslint-disable-next-line no-undef
    cv.copyMakeBorder(dst, dst, 0, dst.cols - dst.rows, 0, 0, cv.BORDER_CONSTANT);
  } else {
    m_x_size = faceDetectionImageMinWidth;
    m_y_size = Math.round(m_x_size * ((1 * f_height) / f_width));
    m_c_size = 3;
    // eslint-disable-next-line no-undef
    dsize = new cv.Size(m_x_size, m_y_size);
  }
  // eslint-disable-next-line no-undef
  cv.resize(dst, in_dst, dsize);

  const dstRGBA = dst.clone();
  // eslint-disable-next-line no-undef
  cv.cvtColor(dstRGBA, dstRGBA, cv.COLOR_RGB2RGBA);
  const dstRGBAData = new ImageData(
    new Uint8ClampedArray(dstRGBA.data, dstRGBA.cols, dstRGBA.rows),
    dstRGBA.cols,
    dstRGBA.rows,
  );

  const in_dstRGBA = in_dst.clone();
  // eslint-disable-next-line no-undef
  cv.cvtColor(in_dstRGBA, in_dstRGBA, cv.COLOR_RGB2RGBA);
  const in_dstRGBAData = new ImageData(
    new Uint8ClampedArray(in_dstRGBA.data, in_dstRGBA.cols, in_dstRGBA.rows),
    in_dstRGBA.cols,
    in_dstRGBA.rows,
  );

  const returnTensors = false; // Pass in `true` to get tensors back, rather than values.
  const tensor = tf.tensor3d(new Uint8Array(in_dst.data), [m_y_size, m_x_size, m_c_size], 'float32');
  const predictions = await estimateFaces(faceModel, tensor, returnTensors, false);

  let break_condition = !predictions || predictions.length !== 1;

  //Move min face box height and width to config
  const minFaceSize = 10;
  if (!break_condition) {
    let _w = predictions[0].bottomRight[0] - predictions[0].topLeft[0];
    let _h = predictions[0].bottomRight[1] - predictions[0].topLeft[1];
    break_condition = break_condition || _w < minFaceSize || _h < minFaceSize;
  }

  if (break_condition) {
    //Clear Opencv
    src.delete();
    dst.delete();
    in_dst.delete();
    dstRGBA.delete();
    //Clear TFJS
    tensor.dispose();

    return {
      imageData: dstRGBAData,
      resizedImageData: in_dstRGBAData,
      predictions,
      temperature,
      bbCoordinates,
    };
  }

  //Move min face box height and width to config
  // let _w = predictions[0].bottomRight[0] - predictions[0].topLeft[0];
  // let _h = predictions[0].bottomRight[1] - predictions[0].topLeft[1];
  let scale_factor = (1 * f_width) / m_x_size;
  // eslint-disable-next-line no-undef
  let face_size = new cv.Size(faceDetectionImageMinWidth, faceDetectionImageMinHeight);
  let face_thermal;
  // eslint-disable-next-line no-undef
  let face_alined_thermal_show = new cv.Mat();

  // With face alignment there is some issue with accuracy.
  // It should be checked when we have env with thermal USB camera to be able to debug it
  let align_face = false;
  if (align_face) {
    let M = getSimilarityTransformation(predictions[0].landmarks, transformation, scale_factor);
    // eslint-disable-next-line no-undef
    let cv_M = cv.matFromArray(3, 3, cv.CV_64F, M.data);
    // eslint-disable-next-line no-undef
    face_thermal = new cv.Mat();
    // eslint-disable-next-line no-undef
    cv.warpPerspective(src, face_thermal, cv_M, face_size, cv.INTER_LINEAR, cv.BORDER_CONSTANT, new cv.Scalar());
    cv_M.delete();
    //Clear Objects
    M = null;
  } else {
    const face_box_x1y1y1y2 = predictions[0].topLeft.concat(predictions[0].bottomRight);
    const face_box_x1y1y1y2_scaled = face_box_x1y1y1y2.map(function (element) {
      return Math.round(element * scale_factor);
    });
    face_thermal = getROI(src, face_box_x1y1y1y2_scaled);
  }

  // eslint-disable-next-line no-undef
  cv.normalize(face_thermal, face_alined_thermal_show, 0, 255, cv.NORM_MINMAX, cv.CV_8U);
  // eslint-disable-next-line no-undef
  cv.cvtColor(face_alined_thermal_show, face_alined_thermal_show, cv.COLOR_GRAY2RGB);

  if (bbCoordinates) {
    // let bb_x1y1x2y2 = [85, 120, 110, 150];
    let bb_x1y1x2y2 = bbCoordinates;
    let frame_bb = getROI(src, bb_x1y1x2y2);
    // eslint-disable-next-line no-undef
    cv.normalize(frame_bb, frame_bb, 0, 255, cv.NORM_MINMAX, cv.CV_8U);
    // eslint-disable-next-line no-undef
    cv.cvtColor(frame_bb, frame_bb, cv.COLOR_GRAY2RGB);

    // TODO: Here we can show black body
    // # Thermal config
    // T0 = 1483
    // A = 9.598693e-7
    // R = 1.0
    // C = 50
    // max_temperature = 105.0
    // min_temperature = 95.0

    // Угол наклона
    let config_A = 9.598693e-7;
    // ???
    let config_T0 = 1483;
    // BB temperature (37), 273.15 - it is absolute 0
    let config_BBT = 37.0 + 273.15;
    // Let's use this value to tune the results, possible values are [0.98...1]
    let config_R = 1.0;
    // Histogram cut parameter
    let config_C = 50;
    let coeff_B = _calculate_B(src, bb_x1y1x2y2, config_A, config_T0, config_BBT);
    temperature = _calculate_hist(face_thermal, coeff_B, config_A, config_C, config_T0, config_R);

    frame_bb.delete();
  }

  face_thermal.delete();
  face_alined_thermal_show.delete();
  tensor.dispose();
  in_dst.delete();
  dst.delete();
  dstRGBA.delete();
  src.delete();

  return {
    imageData: dstRGBAData,
    resizedImageData: in_dstRGBAData,
    predictions,
    temperature,
    bbCoordinates,
  };
};
